import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Circle, Line, Text } from 'fabric';
import {
  addVertex,
  removeLastVertex,
  createPolygon,
  calculateDistance,
  isNearPoint,
  applyHierarchicalSnap,
  hasSelfIntersection,
} from './PolygonTool';
import { useCommand } from '../../../context/CommandContext';
import { useGardenSettings } from '../../../context/GardenSettingsContext';
import { CreateObjectCommand } from '../../../commands/CreateObjectCommand';
import { useTouchDeferredVertex } from './useTouchDeferredVertex';
import DrawingToolActions from './DrawingToolActions';

/**
 * Polygon Drawing Tool Component
 *
 * Allows users to draw freeform polygons by clicking to place vertices.
 * Supports snap-to-grid, snap-to-endpoint, auto-close, and undo/cancel.
 *
 * Features:
 * - Hierarchical snapping (endpoint → grid → raw)
 * - Self-intersection validation (prevents crossing edges)
 * - Vertex limit (50 warning, 100 hard limit)
 * - Edge labels in meters (using calibration)
 * - Visual feedback (vertex markers, preview line, snap indicators)
 * - Keyboard shortcuts (Backspace, Escape, Enter, Ctrl)
 * - Command Pattern integration for undo/redo
 */
const PolygonDrawingTool = ({
  canvas,
  gridSize,
  snapEnabled,
  selectedObjectType,
  onComplete,
  onCancel,
}) => {
  const [vertices, setVertices] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [mousePos, setMousePos] = useState(null);
  const [mousePosScreen, setMousePosScreen] = useState(null); // Screen coordinates for tooltip
  const [ctrlPressed, setCtrlPressed] = useState(false);
  const [warningToast, setWarningToast] = useState(null);
  const [currentEdgeLength, setCurrentEdgeLength] = useState(0); // Current edge being drawn

  const { commandStack } = useCommand();
  const { settings } = useGardenSettings();

  // Refs for visual objects
  const vertexMarkersRef = useRef([]);
  const edgeLinesRef = useRef([]);
  const edgeLabelsRef = useRef([]);
  const previewLineRef = useRef(null);
  // Snap indicator lives in a ref, not in state: mouse:move fires far more often
  // than React re-renders, so a state-held reference is stale for every move
  // within the same render batch — the old indicator never gets removed and
  // orange rings pile up on the canvas (#682).
  const snapIndicatorRef = useRef(null);

  // Live mirror of vertices so event handlers read the current value without
  // relying on closure re-subscription timing (needed for the dblclick path).
  const verticesRef = useRef(vertices);
  useEffect(() => {
    verticesRef.current = vertices;
  }, [vertices]);

  // Whether the most recent single (detail === 1) mouse:down actually placed a
  // new vertex. The dblclick handler uses this to decide whether to drop the
  // vertex its own first click added before closing (#462).
  const dblclickAddedVertexRef = useRef(false);

  // Constants
  const CLOSE_PROXIMITY = 10; // Pixels to auto-close
  const ENDPOINT_SNAP_DISTANCE = 10; // Pixels for endpoint snap
  const FIRST_VERTEX_SIZE = 8;
  const VERTEX_SIZE = 6;
  const VERTEX_LIMIT_SOFT = 50; // Show warning
  const VERTEX_LIMIT_HARD = 100; // Prevent adding more

  // Get calibration for edge labels (pixels per meter)
  const pixelsPerMeter = settings?.backgroundImage?.calibration?.scale || null;

  /**
   * Format distance for edge labels (meters if calibrated, cm otherwise)
   */
  const formatDistance = useCallback((distanceInPixels) => {
    if (pixelsPerMeter && pixelsPerMeter > 0) {
      const meters = distanceInPixels / pixelsPerMeter;
      return `${meters.toFixed(2)}m`;
    }
    // Fallback: treat pixels as cm (1 pixel = 1 cm)
    return `${distanceInPixels.toFixed(0)}cm`;
  }, [pixelsPerMeter]);

  /**
   * Show warning toast
   */
  const showWarning = useCallback((message) => {
    setWarningToast(message);
    setTimeout(() => setWarningToast(null), 3000); // Hide after 3 seconds
  }, []);

  /**
   * Remove the current snap indicator, if any.
   */
  const removeSnapIndicator = useCallback(() => {
    if (snapIndicatorRef.current) {
      canvas?.remove(snapIndicatorRef.current);
      snapIndicatorRef.current = null;
    }
  }, [canvas]);

  /**
   * Clear all visual feedback objects
   */
  const clearVisuals = useCallback(() => {
    if (!canvas) return;

    vertexMarkersRef.current.forEach(marker => canvas.remove(marker));
    edgeLinesRef.current.forEach(line => canvas.remove(line));
    edgeLabelsRef.current.forEach(label => canvas.remove(label));

    if (previewLineRef.current) {
      canvas.remove(previewLineRef.current);
      previewLineRef.current = null;
    }

    removeSnapIndicator();

    vertexMarkersRef.current = [];
    edgeLinesRef.current = [];
    edgeLabelsRef.current = [];

    // Safety net: drop anything still flagged temporary. Only one drawing tool
    // is mounted at a time, so every remaining marker belongs to this run —
    // without the sweep a leaked helper stays on the canvas and gets persisted.
    canvas.getObjects()
      .filter(obj => obj.data?.isTemporary)
      .forEach(obj => canvas.remove(obj));

    canvas.requestRenderAll();
  }, [canvas, removeSnapIndicator]);

  /**
   * Close the polygon with the given vertices: validate, create the Fabric
   * object via the undo/redo command, clear the in-progress visuals and reset
   * drawing state. Shared by the auto-close (click near first vertex), the
   * Enter key, and the double-click close paths (#462).
   *
   * @param {Array<{x: number, y: number}>} verts - Final vertices
   * @returns {boolean} True if the polygon was closed, false if rejected
   */
  const closePolygon = useCallback((verts) => {
    if (!canvas || !verts || verts.length < 3) return false;

    if (hasSelfIntersection(verts)) {
      showWarning('Cannot close polygon: edges would cross.');
      return false;
    }

    const polygonObject = createPolygon(verts, false, selectedObjectType);

    // Command for undo/redo (command will add the object to canvas)
    const command = new CreateObjectCommand(canvas, polygonObject);
    commandStack.execute(command);

    clearVisuals();
    setVertices([]);
    setIsDrawing(false);
    onComplete?.(polygonObject);
    return true;
  }, [canvas, commandStack, clearVisuals, onComplete, selectedObjectType, showWarning]);

  /**
   * Render vertex markers
   */
  const renderVertices = useCallback(() => {
    // Remove old markers
    vertexMarkersRef.current.forEach(marker => canvas?.remove(marker));
    vertexMarkersRef.current = [];

    vertices.forEach((vertex, index) => {
      const isFirst = index === 0;
      const isNear = mousePos && isFirst && isNearPoint(mousePos, vertex, CLOSE_PROXIMITY);

      const marker = new Circle({
        left: vertex.x,
        top: vertex.y,
        radius: isNear ? FIRST_VERTEX_SIZE + 2 : (isFirst ? FIRST_VERTEX_SIZE : VERTEX_SIZE),
        fill: isNear ? '#FFA500' : '#10B981', // Orange when near, emerald otherwise
        stroke: '#fff',
        strokeWidth: 2,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        data: { isTemporary: true },
      });

      canvas?.add(marker);
      vertexMarkersRef.current.push(marker);
    });
  }, [canvas, vertices, mousePos]);

  /**
   * Render edge lines between vertices
   */
  const renderEdges = useCallback(() => {
    // Remove old edges
    edgeLinesRef.current.forEach(line => canvas?.remove(line));
    edgeLabelsRef.current.forEach(label => canvas?.remove(label));
    edgeLinesRef.current = [];
    edgeLabelsRef.current = [];

    for (let i = 0; i < vertices.length - 1; i++) {
      const start = vertices[i];
      const end = vertices[i + 1];

      // Edge line
      const line = new Line([start.x, start.y, end.x, end.y], {
        originX: 'left',
        originY: 'top',
        stroke: '#10B981',
        strokeWidth: 2,
        selectable: false,
        evented: false,
        data: { isTemporary: true },
      });
      canvas?.add(line);
      edgeLinesRef.current.push(line);

      // Edge label (distance in meters or cm)
      const distance = calculateDistance(start, end);
      const midX = (start.x + end.x) / 2;
      const midY = (start.y + end.y) / 2;

      const label = new Text(formatDistance(distance), {
        left: midX,
        top: midY - 10,
        fontSize: 12,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 'bold',
        fill: '#10B981',
        backgroundColor: '#FFFFFF',
        padding: 3,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        data: { isTemporary: true, isMeasurementLabel: true },
      });
      canvas?.add(label);
      edgeLabelsRef.current.push(label);
    }
  }, [canvas, vertices, formatDistance]);

  /**
   * Render preview line from last vertex to mouse
   */
  const renderPreview = useCallback(() => {
    if (!mousePos || vertices.length === 0) {
      if (previewLineRef.current) {
        canvas?.remove(previewLineRef.current);
        previewLineRef.current = null;
      }
      return;
    }

    const lastVertex = vertices[vertices.length - 1];
    const isNearFirst = vertices.length >= 3 && isNearPoint(mousePos, vertices[0], CLOSE_PROXIMITY);

    if (previewLineRef.current) {
      canvas?.remove(previewLineRef.current);
    }

    previewLineRef.current = new Line(
      [lastVertex.x, lastVertex.y, mousePos.x, mousePos.y],
      {
        originX: 'left',
        originY: 'top',
        stroke: isNearFirst ? '#FFA500' : '#10B981', // Orange when near first vertex
        strokeWidth: 2,
        strokeDashArray: isNearFirst ? [] : [5, 5], // Solid when near first, dashed otherwise
        selectable: false,
        evented: false,
        data: { isTemporary: true },
      }
    );

    canvas?.add(previewLineRef.current);
  }, [canvas, mousePos, vertices]);

  /**
   * Render snap indicator (circle around snap target)
   */
  const renderSnapIndicator = useCallback((point, target) => {
    // Remove old indicator
    removeSnapIndicator();

    if (!target) return;

    const indicator = new Circle({
      left: point.x - 6,
      top: point.y - 6,
      originX: 'left',
      originY: 'top',
      radius: 6,
      fill: 'transparent',
      stroke: '#FFA500', // Orange for endpoint snap
      strokeWidth: 2,
      selectable: false,
      evented: false,
      data: { isTemporary: true },
    });

    canvas?.add(indicator);
    snapIndicatorRef.current = indicator;
  }, [canvas, removeSnapIndicator]);

  /**
   * Update all visuals
   */
  useEffect(() => {
    if (!canvas) return;

    // eslint-disable-next-line react-hooks/immutability -- imperative Fabric render-batching flag; restored to true two lines below
    canvas.renderOnAddRemove = false;
    renderVertices();
    renderEdges();
    renderPreview();
    canvas.renderOnAddRemove = true;
    canvas.renderAll();
  }, [canvas, renderVertices, renderEdges, renderPreview]);

  /**
   * Set crosshair cursor
   */
  useEffect(() => {
    if (!canvas) return;

    // eslint-disable-next-line react-hooks/immutability -- imperative Fabric cursor config; reverted in cleanup
    canvas.defaultCursor = 'crosshair';
    canvas.setCursor('crosshair');

    return () => {
      canvas.defaultCursor = 'default';
      canvas.setCursor('default');
    };
  }, [canvas]);

  /**
   * Commit a vertex at the given (already snapped) point. Shared by the mouse
   * path (mouse:down) and the touch path (mouse:up, via useTouchDeferredVertex).
   */
  const commitVertex = useCallback((snappedPoint) => {
    if (vertices.length >= VERTEX_LIMIT_HARD) {
      showWarning(`Maximum vertex limit reached (${VERTEX_LIMIT_HARD}). Please close the polygon.`);
      return;
    }

    const result = addVertex(vertices, snappedPoint, false, gridSize, snapEnabled);

    if (result.isDuplicate) {
      return; // Skip duplicate vertices
    }

    // Reject a vertex that makes the open polyline cross itself (#462).
    if (result.vertices.length >= 3 && hasSelfIntersection(result.vertices, false)) {
      showWarning('Polygon edges cannot cross. Please adjust vertices.');
      return;
    }

    if (result.shouldClose) {
      closePolygon(result.vertices);
    } else {
      dblclickAddedVertexRef.current = true;
      setVertices(result.vertices);
      setIsDrawing(true);
      if (result.vertices.length === VERTEX_LIMIT_SOFT) {
        showWarning('Large polygon detected. Consider simplifying for better performance.');
      }
    }
  }, [vertices, gridSize, snapEnabled, closePolygon, showWarning]);

  // Clear the preview edge when a pinch cancels a pending touch point (#747).
  const clearTouchPreview = useCallback(() => setMousePos(null), []);
  const { deferIfTouch, updatePoint } = useTouchDeferredVertex(canvas, commitVertex, clearTouchPreview);

  /**
   * Handle mouse move (update preview)
   */
  useEffect(() => {
    if (!canvas) return;

    const handleMouseMove = (e) => {
      const pointer = canvas.getScenePoint(e.e);

      // Apply hierarchical snap
      const snapResult = applyHierarchicalSnap(
        pointer,
        canvas,
        gridSize,
        snapEnabled,
        ctrlPressed,
        ENDPOINT_SNAP_DISTANCE
      );

      setMousePos(snapResult.point);
      updatePoint(snapResult.point); // #747: keep the touch commit-on-lift target current
      setMousePosScreen({ x: e.e.clientX, y: e.e.clientY }); // Store screen coords for tooltip

      // Calculate current edge length if we have at least one vertex
      if (vertices.length > 0) {
        const lastVertex = vertices[vertices.length - 1];
        const edgeLengthPx = calculateDistance(lastVertex, snapResult.point);
        setCurrentEdgeLength(edgeLengthPx);
      } else {
        setCurrentEdgeLength(0);
      }

      // Show snap indicator for endpoint snap
      if (snapResult.snappedTo === 'endpoint') {
        renderSnapIndicator(snapResult.point, snapResult.target);
      } else {
        removeSnapIndicator();
      }
    };

    canvas.on('mouse:move', handleMouseMove);
    return () => canvas.off('mouse:move', handleMouseMove);
  }, [canvas, gridSize, snapEnabled, ctrlPressed, renderSnapIndicator, removeSnapIndicator, vertices, updatePoint]);

  /**
   * Handle mouse down (add vertex or close polygon)
   */
  useEffect(() => {
    if (!canvas) return;

    const handleMouseDown = (e) => {
      // Second click of a double-click: don't add a vertex. The mouse:dblclick
      // handler closes the polygon (#462). The first click (detail === 1) still
      // places its vertex, which the dblclick handler removes before closing.
      if (e.e?.detail >= 2) {
        return;
      }

      // Reset per-click tracking; set true below only when a vertex is placed.
      dblclickAddedVertexRef.current = false;

      const pointer = canvas.getScenePoint(e.e);

      // Apply hierarchical snap
      const snapResult = applyHierarchicalSnap(
        pointer,
        canvas,
        gridSize,
        snapEnabled,
        ctrlPressed,
        ENDPOINT_SNAP_DISTANCE
      );

      const snappedPoint = snapResult.point;

      // Touch: defer placement to lift so the point can be dragged into position
      // first — touch has no hover to preview before a tap (#747).
      if (deferIfTouch(e.e, snappedPoint)) {
        setMousePos(snappedPoint);
        return;
      }

      commitVertex(snappedPoint);
    };

    canvas.on('mouse:down', handleMouseDown);
    return () => canvas.off('mouse:down', handleMouseDown);
  }, [canvas, gridSize, snapEnabled, ctrlPressed, commitVertex, deferIfTouch]);

  /**
   * Handle double-click to close the polygon (#462)
   *
   * A double-click fires two mouse:down events: the first (detail === 1) places
   * a vertex, the second (detail === 2) is suppressed in handleMouseDown. Here
   * we drop the vertex the first click added — unless it was a duplicate/auto-
   * close that placed nothing (e.g. double-clicking on the last vertex) — and
   * then close via the shared path. Requires >= 3 remaining vertices.
   */
  useEffect(() => {
    if (!canvas) return;

    const handleDoubleClick = () => {
      let verts = verticesRef.current;
      if (dblclickAddedVertexRef.current) {
        verts = verts.slice(0, -1);
      }
      if (verts.length < 3) return;
      closePolygon(verts);
    };

    canvas.on('mouse:dblclick', handleDoubleClick);
    return () => canvas.off('mouse:dblclick', handleDoubleClick);
  }, [canvas, closePolygon]);

  /**
   * Handle keyboard shortcuts
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if typing in input field
      const isTyping = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) ||
                       document.activeElement?.contentEditable === 'true';
      if (isTyping) return;

      if (e.key === 'Control') {
        setCtrlPressed(true);
      }

      if (e.key === 'Backspace' && vertices.length > 0) {
        e.preventDefault();
        const newVertices = removeLastVertex(vertices);
        setVertices(newVertices);
        if (newVertices.length === 0) {
          setIsDrawing(false);
        }
      }

      if (e.key === 'Escape' && isDrawing) {
        clearVisuals();
        setVertices([]);
        setIsDrawing(false);
        onCancel?.();
      }

      if (e.key === 'Enter' && vertices.length >= 3) {
        // Manual close with Enter key (self-intersection check inside)
        closePolygon(vertices);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Control') {
        setCtrlPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [vertices, isDrawing, clearVisuals, onCancel, closePolygon]);

  /**
   * Cleanup snap indicator on mouse leave
   */
  useEffect(() => {
    if (!canvas) return;

    const handleMouseOut = () => {
      if (snapIndicatorRef.current) {
        removeSnapIndicator();
        canvas.requestRenderAll();
      }
    };

    canvas.on('mouse:out', handleMouseOut);
    return () => canvas.off('mouse:out', handleMouseOut);
  }, [canvas, removeSnapIndicator]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      clearVisuals();
    };
  }, [clearVisuals]);

  // Format edge length for tooltip (same as edge labels)
  const formatEdgeLength = (lengthInPixels) => {
    if (pixelsPerMeter && pixelsPerMeter > 0) {
      const meters = lengthInPixels / pixelsPerMeter;
      return `${meters.toFixed(2)} m`;
    }
    // Fallback: treat pixels as cm (1 pixel = 1 cm)
    const cm = lengthInPixels;
    return `${cm.toFixed(0)} cm`;
  };

  // Touch: no Enter/Escape — cancel the whole in-progress polygon (#747).
  const handleTouchCancel = useCallback(() => {
    clearVisuals();
    setVertices([]);
    setIsDrawing(false);
    onCancel?.();
  }, [clearVisuals, onCancel]);

  return (
    <>
      {/* Warning toast */}
      {warningToast && (
        <div
          className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded shadow-lg z-50"
          role="alert"
        >
          {warningToast}
        </div>
      )}

      {/* Dimension tooltip - shows current edge length while drawing */}
      {isDrawing && vertices.length > 0 && mousePosScreen && (
        <div
          style={{
            position: 'fixed',
            left: `${mousePosScreen.x + 15}px`,
            top: `${mousePosScreen.y + 15}px`,
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          <div className="bg-white/95 backdrop-blur-sm border-2 border-emerald-500 rounded shadow-lg px-3 py-2">
            <div className="text-sm font-semibold text-gray-900 text-center">
              {formatEdgeLength(currentEdgeLength)}
            </div>
            <div className="text-xs text-gray-500 text-center mt-1">
              {vertices.length} {vertices.length === 1 ? 'Punkt' : 'Punkte'}
            </div>
          </div>
        </div>
      )}
      <DrawingToolActions
        canFinish={vertices.length >= 3}
        onFinish={() => closePolygon(verticesRef.current)}
        onCancel={handleTouchCancel}
      />
    </>
  );
};

PolygonDrawingTool.propTypes = {
  canvas: PropTypes.object,
  gridSize: PropTypes.number,
  snapEnabled: PropTypes.bool,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
};

PolygonDrawingTool.defaultProps = {
  gridSize: 10,
  snapEnabled: true,
  onComplete: null,
  onCancel: null,
};

export default PolygonDrawingTool;
